import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, Clock, Lock, Camera, AlertTriangle, ShieldCheck, XCircle } from 'lucide-react';

const STAGES = [
  { key: 'assigned', label: 'TL Assigned', step: 1 },
  { key: 'tl_verified', label: 'TL Verified', step: 2 },
  { key: 'manager_verified', label: 'Manager Approved', step: 3 },
  { key: 'shreya_verified', label: 'Shreya QA Check', step: 4, isOpsGate: true },
  { key: 'admin_approved', label: 'Vishal Final Approval', step: 5, isAdminGate: true },
  { key: 'ops_delivered', label: 'Delivered to Gym', step: 6, hasPhoto: true },
  { key: 'customer_received', label: 'Customer Picked Up', step: 7, hasPhoto: true },
];

const STATUS_ORDER = {
  assigned: 1,
  tl_verified: 2,
  manager_verified: 3,
  shreya_verified: 4,
  admin_approved: 5,
  ops_delivered: 6,
  customer_received: 7,
  cancelled: 0,
};

const formatIST = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

function getSlaInfo(timeline) {
  if (!timeline?.sla_deadline) return null;
  const currentStep = STATUS_ORDER[timeline.current_status] || 0;
  if (currentStep < 5 || currentStep >= 7) return null;
  const now = new Date();
  const deadline = new Date(timeline.sla_deadline);
  const diffMs = deadline - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return { overdue: true, text: `OVERDUE by ${Math.abs(diffDays)} days`, dueDate: formatIST(timeline.sla_deadline) };
  }
  return { overdue: false, text: `${diffDays} days remaining`, dueDate: formatIST(timeline.sla_deadline) };
}

export default function VisualTimeline({ assignmentId, timelineData }) {
  const [timeline, setTimeline] = useState(timelineData || null);
  const [loading, setLoading] = useState(!timelineData && !!assignmentId);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (timelineData) {
      setTimeline(timelineData);
      return;
    }
    if (!assignmentId) return;
    let cancelled = false;
    setLoading(true);
    axiosInstance.get(`/supplements/assignments/${assignmentId}/timeline`)
      .then(res => {
        if (!cancelled) setTimeline(res.data);
      })
      .catch(() => {
        if (!cancelled) setTimeline(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [assignmentId, timelineData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!timeline) {
    return (
      <div className="text-center py-8 text-slate-400">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
        <p>Timeline data unavailable</p>
      </div>
    );
  }

  const currentStep = STATUS_ORDER[timeline.current_status] || 0;
  const isCancelled = timeline.current_status === 'cancelled';
  const sla = getSlaInfo(timeline);
  const stages = timeline.stages || [];

  const getStageState = (stageKey) => {
    const stageStep = STATUS_ORDER[stageKey] || 0;
    const stageData = stages.find(s => s.status === stageKey);
    if (isCancelled) {
      if (stageData?.completed) return 'complete';
      return 'locked';
    }
    if (stageData?.completed) return 'complete';
    if (stageStep === currentStep + 1 || (stageStep === 1 && currentStep >= 1)) return 'pending';
    if (stageStep <= currentStep) return 'complete';
    return 'locked';
  };

  return (
    <div>
      {/* SLA Indicator */}
      {sla && (
        <div className={`mb-4 p-3 rounded-lg border ${sla.overdue ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Delivery SLA</span>
            <Badge className={sla.overdue ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-green-100 text-green-700'}>
              {sla.text}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1">Due: {sla.dueDate}</p>
        </div>
      )}

      {/* Cancelled Banner */}
      {isCancelled && (
        <div className="mb-4 p-3 rounded-lg border bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-medium">Assignment Cancelled</span>
          </div>
          {timeline.cancel_reason && (
            <p className="text-sm text-red-600 mt-1">Reason: {timeline.cancel_reason}</p>
          )}
        </div>
      )}

      {/* Timeline Steps */}
      <div className="space-y-0">
        {STAGES.map((stage, idx) => {
          const state = getStageState(stage.key);
          const stageData = stages.find(s => s.status === stage.key) || {};
          const isLast = idx === STAGES.length - 1;

          const hasRejection = (stage.isAdminGate || stage.isOpsGate) && stageData.rejection_count > 0 && !stageData.completed;

          let borderColor = 'border-gray-200';
          let bgColor = 'bg-gray-50';
          let iconColor = 'text-gray-400';
          let Icon = Lock;

          if (state === 'complete') {
            if (stage.isAdminGate) {
              borderColor = 'border-blue-300';
              bgColor = 'bg-blue-50';
              iconColor = 'text-blue-600';
              Icon = ShieldCheck;
            } else if (stage.isOpsGate) {
              borderColor = 'border-orange-300';
              bgColor = 'bg-orange-50';
              iconColor = 'text-orange-600';
              Icon = ShieldCheck;
            } else {
              borderColor = 'border-green-300';
              bgColor = 'bg-green-50';
              iconColor = 'text-green-600';
              Icon = CheckCircle2;
            }
          } else if (hasRejection) {
            borderColor = 'border-red-300';
            bgColor = 'bg-red-50';
            iconColor = 'text-red-600';
            Icon = XCircle;
          } else if (state === 'pending') {
            if (stage.isAdminGate) {
              borderColor = 'border-blue-200';
              bgColor = 'bg-blue-50';
              iconColor = 'text-blue-500';
              Icon = ShieldCheck;
            } else if (stage.isOpsGate) {
              borderColor = 'border-orange-200';
              bgColor = 'bg-orange-50';
              iconColor = 'text-orange-500';
              Icon = ShieldCheck;
            } else {
              borderColor = 'border-yellow-300';
              bgColor = 'bg-yellow-50';
              iconColor = 'text-yellow-600';
              Icon = Clock;
            }
          }

          return (
            <div key={stage.key}>
              {/* Step Card */}
              <div className={`border-2 ${borderColor} ${bgColor} rounded-lg p-4`}>
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>
                    <Icon className="w-5 h-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-400">STEP {stage.step}</span>
                      <span className="font-medium text-slate-800">{stage.label}</span>
                    </div>

                    {state === 'complete' && (
                      <div className="mt-1 text-sm text-slate-600">
                        <div>By: {stageData.actor_name || '—'}</div>
                        <div className="text-xs text-slate-400">{formatIST(stageData.timestamp)}</div>
                        {stageData.notes && (
                          <div className="mt-1 text-xs text-slate-500 italic">"{stageData.notes}"</div>
                        )}
                      </div>
                    )}

                    {state === 'pending' && !hasRejection && (
                      <div className={`mt-1 text-sm ${stage.isAdminGate ? 'text-blue-600' : stage.isOpsGate ? 'text-orange-600' : 'text-yellow-700'}`}>
                        {stage.isAdminGate ? 'Awaiting Vishal\'s approval' : stage.isOpsGate ? 'Awaiting Shreya\'s QA check' : `Awaiting ${stage.label.toLowerCase()}`}
                      </div>
                    )}

                    {hasRejection && (
                      <div className="mt-1 space-y-1">
                        <div className="text-sm text-red-700 font-medium">
                          Rejected {stageData.rejection_count}x — awaiting re-submission
                        </div>
                        <div className="text-xs text-red-600 italic">
                          "{stageData.last_rejection_reason}"
                        </div>
                        <div className="text-xs text-red-400">
                          By {stageData.last_rejected_by_name} — {formatIST(stageData.last_rejected_at)}
                        </div>
                      </div>
                    )}

                    {stage.isOpsGate && !hasRejection && stageData.rejection_count > 0 && (
                      <div className="text-xs text-red-600 mt-1">
                        Rejected {stageData.rejection_count}x · "{stageData.last_rejection_reason}" — {stageData.last_rejected_by_name}
                      </div>
                    )}
                    {stage.isAdminGate && !hasRejection && stageData.rejection_count > 0 && (
                      <div className="text-xs text-red-600 mt-1">
                        Rejected {stageData.rejection_count}x · "{stageData.last_rejection_reason}" — {stageData.last_rejected_by_name}
                      </div>
                    )}

                    {state === 'locked' && (
                      <div className="mt-1 text-sm text-gray-400">
                        Locked — previous steps required
                      </div>
                    )}

                    {/* Photo Display (stages 6 and 7) — photo_url already contains the full /api/... path */}
                    {stage.hasPhoto && state === 'complete' && stageData.photo_url && (
                      <div className="mt-2">
                        <img
                          src={stageData.photo_url}
                          alt={`${stage.label} proof`}
                          className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setPhotoPreview(stageData.photo_url)}
                        />
                      </div>
                    )}

                    {stage.hasPhoto && state === 'pending' && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-slate-400">
                        <Camera className="w-3 h-3" />
                        Photo required
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Connector Line */}
              {!isLast && (
                <div className="flex justify-start ml-6">
                  <div className={`w-0.5 h-4 ${state === 'complete' ? 'bg-green-300' : 'bg-gray-200'}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Photo Preview Dialog */}
      <Dialog open={!!photoPreview} onOpenChange={() => setPhotoPreview(null)}>
        <DialogContent className="max-w-lg p-2">
          {photoPreview && (
            <img src={photoPreview} alt="Proof photo" className="w-full rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
